"use client";

import { useState, useRef, useEffect, type FocusEvent } from "react";
import { Volume2, Volume1, VolumeX, Headphones, HeadphoneOff } from "lucide-react";
import { useSoundContextOptional } from "@/contexts/SoundContext";

/**
 * Sound controls with:
 * - Main button: global mute (desktop click / touch second tap)
 * - Popover with per-channel controls:
 *   - Sound effects: mute toggle + volume slider
 *   - Music: enable toggle + volume slider
 */
export default function SoundToggle() {
  const soundContext = useSoundContextOptional();
  const [showSlider, setShowSlider] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pointerDownRef = useRef(false);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Close slider when clicking outside
  useEffect(() => {
    if (!showSlider) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSlider(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSlider]);

  // Don't render if no sound context
  if (!soundContext) {
    return null;
  }

  const { isGlobalMuted, toggleGlobalMute, volume, setVolume, isMuted, toggleMute, musicEnabled, musicVolume, setMusicVolume, toggleMusic } = soundContext;

  // Speaker icon based on global mute and per-channel states
  const getSpeakerIcon = () => {
    if (isGlobalMuted) {
      return <VolumeX className="w-5 h-5 md:w-6 md:h-6" />;
    }
    const anySoundActive = (!isMuted && volume > 0) || musicEnabled;
    if (!anySoundActive) {
      return <VolumeX className="w-5 h-5 md:w-6 md:h-6" />;
    }
    const effectiveLevel = Math.max(isMuted ? 0 : volume, musicEnabled ? musicVolume : 0);
    if (effectiveLevel < 0.5) {
      return <Volume1 className="w-5 h-5 md:w-6 md:h-6" />;
    }
    return <Volume2 className="w-5 h-5 md:w-6 md:h-6" />;
  };

  const handleMouseEnter = () => {
    if (window.matchMedia("(hover: hover)").matches) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setShowSlider(true);
    }
  };

  const handleMouseLeave = () => {
    if (window.matchMedia("(hover: hover)").matches) {
      if (containerRef.current?.contains(document.activeElement)) {
        return;
      }
      timeoutRef.current = setTimeout(() => {
        setShowSlider(false);
      }, 300);
    }
  };

  const handleFocusCapture = () => {
    if (pointerDownRef.current) {
      return;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShowSlider(true);
  };

  const handleBlurCapture = (e: FocusEvent<HTMLDivElement>) => {
    pointerDownRef.current = false;
    const nextFocused = e.relatedTarget;
    if (nextFocused && containerRef.current?.contains(nextFocused)) {
      return;
    }
    setShowSlider(false);
  };

  const handleVolumeButtonClick = () => {
    pointerDownRef.current = false;
    if (window.matchMedia("(hover: none)").matches) {
      if (!showSlider) {
        setShowSlider(true);
      } else {
        toggleGlobalMute();
      }
    } else {
      toggleGlobalMute();
    }
  };

  return (
    <div className="flex items-center gap-1">
      {/* Volume/Mute Control with Slider */}
      <div 
        ref={containerRef}
        className="relative flex items-center"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
      >
        <button
          onPointerDown={() => {
            pointerDownRef.current = true;
          }}
          onPointerUp={() => {
            pointerDownRef.current = false;
          }}
          onPointerCancel={() => {
            pointerDownRef.current = false;
          }}
          onClick={handleVolumeButtonClick}
          className={`p-2 rounded-lg transition-colors ${
            !isGlobalMuted
              ? "text-primary hover:text-accent hover:bg-primary/10"
              : "text-muted hover:text-primary hover:bg-primary/10"
          }`}
          title={isGlobalMuted ? "Unmute All" : "Mute All"}
          aria-label={isGlobalMuted ? "Unmute All" : "Mute All"}
          aria-expanded={showSlider}
          aria-controls="sound-controls-popover"
        >
          {getSpeakerIcon()}
        </button>

        {/* Volume Slider Popover - always below the button */}
        <div 
          id="sound-controls-popover"
          className={`
            absolute z-50 px-3 py-2 
            bg-surface-elevated rounded-lg shadow-lg border border-border
            transition-all duration-200
            top-full right-0 mt-2 origin-top-right
            ${showSlider ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"}
          `}
        >
          <div className={`flex flex-col gap-3 transition-opacity ${isGlobalMuted ? "opacity-40" : ""}`}>
          {/* Sound Effects Volume */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMute}
              className={`p-1.5 rounded transition-colors ${
                !isMuted 
                  ? "text-primary hover:text-primary/80 hover:bg-primary/10" 
                  : "text-muted hover:text-primary hover:bg-primary/10"
              }`}
              aria-label={isMuted ? "Unmute sound effects" : "Mute sound effects"}
            >
              {isMuted ? (
                <VolumeX className="w-4 h-4 opacity-40" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>
            
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="flex-1 h-2 bg-muted/30 rounded-lg appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-4
                [&::-webkit-slider-thumb]:h-4
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-primary
                [&::-webkit-slider-thumb]:shadow-[0_0_8px_var(--color-primary)]
                [&::-webkit-slider-thumb]:cursor-pointer
                [&::-webkit-slider-thumb]:transition-transform
                [&::-webkit-slider-thumb]:hover:scale-110
                [&::-moz-range-thumb]:w-4
                [&::-moz-range-thumb]:h-4
                [&::-moz-range-thumb]:rounded-full
                [&::-moz-range-thumb]:bg-primary
                [&::-moz-range-thumb]:shadow-[0_0_8px_var(--color-primary)]
                [&::-moz-range-thumb]:border-0
                [&::-moz-range-thumb]:cursor-pointer"
              aria-label="Sound Effects Volume"
            />
            <span className="text-xs text-muted w-8 text-right tabular-nums">
              {Math.round(volume * 100)}%
            </span>
          </div>

          {/* Music Volume */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMusic}
              className={`p-1.5 rounded transition-colors ${
                musicEnabled
                  ? "text-accent hover:text-accent/80 hover:bg-accent/10"
                  : "text-muted hover:text-accent hover:bg-accent/10"
              }`}
              title={musicEnabled ? "Turn off music" : "Turn on music"}
              aria-label={musicEnabled ? "Turn off music" : "Turn on music"}
            >
              {musicEnabled ? (
                <Headphones className="w-4 h-4" />
              ) : (
                <HeadphoneOff className="w-4 h-4 opacity-40" />
              )}
            </button>
            
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={musicVolume}
              onChange={(e) => setMusicVolume(parseFloat(e.target.value))}
              className="flex-1 h-2 bg-muted/30 rounded-lg appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-4
                [&::-webkit-slider-thumb]:h-4
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-accent
                [&::-webkit-slider-thumb]:shadow-[0_0_8px_var(--color-accent)]
                [&::-webkit-slider-thumb]:cursor-pointer
                [&::-webkit-slider-thumb]:transition-transform
                [&::-webkit-slider-thumb]:hover:scale-110
                [&::-moz-range-thumb]:w-4
                [&::-moz-range-thumb]:h-4
                [&::-moz-range-thumb]:rounded-full
                [&::-moz-range-thumb]:bg-accent
                [&::-moz-range-thumb]:shadow-[0_0_8px_var(--color-accent)]
                [&::-moz-range-thumb]:border-0
                [&::-moz-range-thumb]:cursor-pointer"
              aria-label="Music Volume"
            />
            <span className="text-xs text-muted w-8 text-right tabular-nums">
              {Math.round(musicVolume * 100)}%
            </span>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
